import type * as Stitches from "@stitches/react";
import React, { useEffect, useState } from "react";
import { mergeCss, styled } from "src/ccl/stitches";
import { OutsideClickHandler } from "src/ccl/util/OutsideClickHandler";

// The panel's fade, and the delay before a closed panel is clipped out of the
// page. One constant so the clip can never land before the fade finishes.
const PANEL_TRANSITION_MS = 250;

const Wrapper = styled("div", {
  position: "relative",
  zIndex: "$400",
});

// Full-size frame for the Body so a closed panel can be clipped out of the
// page's scrollable area without touching the launcher (its focus ring paints
// outside the wrapper box). A closed Body stays in layout (visibility: hidden)
// so it can fade, but it still widened the document: on a phone the header's
// "For you" panel pushed the page to 529px, mobile Chrome zoomed the layout
// viewport out to match, and every fixed or sticky bottom bar landed below the
// screen.
const BodyFrame = styled("div", {
  position: "absolute",
  inset: 0,
  perspective: "800px",
  pointerEvents: "none",

  variants: {
    state: {
      // ponytail: the delayed clip keeps the fade-out where allow-discrete
      // transitions exist; elsewhere a closing panel just disappears at once.
      closed: {
        overflow: "clip",
        transition: `overflow 0s ${PANEL_TRANSITION_MS}ms allow-discrete`,
      },
    },
  },
});

const LauncherLink = styled("span", {
  textDecoration: "none",
  cursor: "pointer",
});

const Body = styled("div", {
  position: "absolute",
  bottom: 0,
  pointerEvents: "auto",
  border: "1px $grey3 solid",
  boxShadow: "$focus",
  transform: "translate(0, calc(100% + 16px)) rotateX(-15deg)",
  transformOrigin: "50% -16px",
  opacity: 0,
  transition: `${PANEL_TRANSITION_MS}ms ease all`,
  visibility: "hidden",
  padding: "$4",
  backgroundColor: "$white",

  variants: {
    position: {
      left: {
        left: 0,
      },
      right: {
        right: 0,
      },
      center: {
        left: "50%",
        transform: "translate(-50%, calc(100% + 16px)) rotateX(-15deg)",
      },
    },
    offset: {
      none: {
        transform: "translate(0, 100%) rotateX(-15deg)",
        transformOrigin: "50% 0",
      },
    },
    state: {
      open: {
        opacity: 1,
        visibility: "visible",
        transform: "translate(0, calc(100% + 16px)) rotateX(0deg)",
      },
      openNoOffset: {
        opacity: 1,
        visibility: "visible",
        transform: "translate(0, 100%) rotateX(0deg)",
      },
    },
  },
  compoundVariants: [
    {
      position: "center",
      state: "open",
      css: {
        transform: "translate(-50%, calc(100% + 16px)) rotateX(0deg)",
      },
    },
  ],
});

export interface DropdownProps {
  launcher: React.ReactNode;
  position?: "left" | "right" | "center";
  bodyCss?: Stitches.CSS;
  wrapperCss?: Stitches.CSS;
  noOffset?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  withHoverEffect?: boolean;
  dropdownOpen?: boolean;
  children?: React.ReactNode;
}

export const Dropdown = ({
  launcher,
  position = "center",
  bodyCss = {},
  wrapperCss = {},
  noOffset = false,
  children,
  onOpen,
  onClose,
  withHoverEffect = false,
  dropdownOpen,
}: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isOpen = open || hovered;

  useEffect(() => {
    if (dropdownOpen !== undefined && withHoverEffect) {
      setOpen(dropdownOpen);
      setHovered(dropdownOpen);
    }
  }, [dropdownOpen]);

  const handleMouseOver = () => {
    !open && setHovered(true);
  };
  const handleMouseLeave = () => {
    !open && setHovered(false);
  };

  return (
    <Wrapper
      css={wrapperCss}
      onMouseOver={() => {
        withHoverEffect && handleMouseOver();
      }}
      onMouseLeave={() => {
        withHoverEffect && handleMouseLeave();
      }}
    >
      <OutsideClickHandler
        onOutsideClick={() => {
          setOpen(false);
          setHovered(false);
          onClose?.();
        }}
      >
        <LauncherLink
          onClick={() => {
            const newState = !open;
            setOpen(newState);
            if (!newState) {
              onClose?.();
            } else {
              onOpen?.();
            }
          }}
        >
          {launcher}
        </LauncherLink>
        <BodyFrame state={isOpen ? undefined : "closed"}>
          <Body
            position={position}
            state={isOpen ? (noOffset ? "openNoOffset" : "open") : undefined}
            css={mergeCss(bodyCss, {})}
            offset={noOffset ? "none" : undefined}
          >
            {children}
          </Body>
        </BodyFrame>
      </OutsideClickHandler>
    </Wrapper>
  );
};
